import React, { useEffect, useState } from "react";
import { Container, Table, Modal, Button } from "react-bootstrap";
import PaginationComponent from "../component/Pagination";
import Loader from "../component/Loader";
import Swal from "sweetalert2";
import "../Assets/css/SolArk.css";
import logoSvg from "../Assets/images/logo.svg";
// import styles from "../Assets/css/electricvehical.module.css";
import styles from "../Assets/css/fleet.module.css";
import Select from "react-select";
import { useData, US_STATES } from "../context/DataContext";

const SolArkDetails = () => {
  const { utilities: contextUtilities, fetchAllData } = useData();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const SOL_ARK_URL = BASE_URL + "devices/unified-list/detail/?type=Sol-Ark";

  const [vehicleData, setvehicleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);

  const [dateError, setDateError] = useState("");
  const [loading, setLoading] = useState({
    global: false,
  });

  // Calculate yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Date + Hour States
  const [startDate, setStartDate] = useState(getYesterdayDate());
  const [startHour, setStartHour] = useState("00:00");

  const [endDate, setEndDate] = useState(getYesterdayDate());
  const [endHour, setEndHour] = useState("23:00");

  // Utility and State Filters
  const [selectedUtility, setSelectedUtility] = useState("All");
  const [selectedState, setSelectedState] = useState("All");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  
  // Command parameters
  const [deviceSN, setDeviceSN] = useState("");
  const [inverterSN, setInverterSN] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const [commandDate, setCommandDate] = useState("");
  const [columnType, setColumnType] = useState("pac");
  const language = "en"; // Hardcoded language

  // --------- FETCH API ---------- //
  const fetchVehicleData = async (start = "", end = "", utility = "All", state = "All") => {
    try {
      setLoading({ global: true });

      const token = localStorage.getItem("accessToken");

      let apiURL = SOL_ARK_URL;
      const params = [];
      
      if (start && end) {
        params.push(`startdate=${start}`);
        params.push(`enddate=${end}`);
      }
      if (utility && utility !== "All") {
        params.push(`utility=${encodeURIComponent(utility)}`);
      }
      if (state && state !== "All") {
        params.push(`state=${encodeURIComponent(state)}`);
      }
      
      if (params.length > 0) {
        apiURL = `${SOL_ARK_URL}&${params.join('&')}`;
      }

      const response = await fetch(apiURL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("API Error");

      const data = await response.json();

      setvehicleData(data.results || []);
      setFilteredData(data.results || []);
    } catch (err) {
      console.error(err);
      setvehicleData([]);
      setFilteredData([]);
    } finally {
      setLoading({ global: false });
    }
  };

  useEffect(() => {
    fetchVehicleData();
  }, []);

  // Fetch utilities if not already loaded in context
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken && !contextUtilities) {
      fetchAllData(storedAccessToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- FILTER -------- //
  const handleFilter = () => {
    if (!startDate || !endDate) {
      setDateError("Please select Start Date and End Date and Hour");
      return;
    }

    setDateError("");

    const startValue = `${startDate.replace(/-/g, '/')}T${startHour || "00:00"}:00`;
    const endValue = `${endDate.replace(/-/g, '/')}T${endHour || "23:00"}:00`;

    fetchVehicleData(startValue, endValue, selectedUtility, selectedState);
    setCurrentPage(1);
  };

  // -------- CLEAR -------- //
  const clearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartHour("");
    setEndHour("");
    setSelectedUtility("All");
    setSelectedState("All");
    setDateError("");

    fetchVehicleData();
  };

  // ------------------------------
  // MODAL HANDLERS
  // ------------------------------
  const handleOpenModal = (device) => {
    setSelectedDevice(device);
    setShowModal(true);
    setDeviceSN(device.plant_id || "");
    // Set first inverter SN if available
    if (device.inverters && device.inverters.length > 0) {
      setInverterSN(device.inverters[0].sn || "");
    } else {
      setInverterSN("");
    }
    setNewAlias("");
    setCommandDate("");
    setColumnType("pac");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDevice(null);
    setDeviceSN("");
    setInverterSN("");
    setNewAlias("");
    setCommandDate("");
    setColumnType("pac");
  };

  // ------------------------------
  // COMMAND HANDLERS
  // ------------------------------
  const sendCommand = async (slug, additionalParams = {}) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Access token missing",
        });
        return;
      }

      const payload = {
        device_id: deviceSN,
        device_type: "sol-ark",
        slug: slug,
        plantId: deviceSN,
        sn: inverterSN,
        lan: language,
        ...additionalParams,
      };

      const response = await fetch(BASE_URL + "devices/send-command/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to send command");

      const result = await response.json();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Command executed successfully!",
        timer: 2000,
      });
      
      // Show result if available
      if (result && Object.keys(result).length > 0) {
        console.log("Command result:", result);
      }
      
      return result;
    } catch (error) {
      console.error("Command Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to execute command. Please try again.",
      });
    }
  };

  const handleSetAlias = async () => {
    if (!deviceSN || !newAlias) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please enter both Device SN and New Alias",
      });
      return;
    }
    await sendCommand("inverter-set-alias", { alias: newAlias });
  };

  const handleDeleteInverter = async () => {
    if (!deviceSN) {
      Swal.fire({
        icon: "warning",
        title: "Required Field",
        text: "Device SN is required",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This will delete the inverter. This action cannot be undone!",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;
    await sendCommand("inverter-delete");
    fetchVehicleData();
  };

  const handleInvertersCount = () => {
    sendCommand("inverters-count", { type: -1 });
  };

  const handleInvertersList = () => {
    sendCommand("inverters-list", { page: 1, limit: 10, type: 1 });
  };

  const handleGetRealtimeOutput = () => {
    if (!inverterSN) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN is required" });
      return;
    }
    sendCommand("inverter-realtime-output");
  };

  const handleGetRealtimeInput = () => {
    if (!inverterSN) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN is required" });
      return;
    }
    sendCommand("inverter-realtime-input");
  };

  const handleGetOutputDay = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date are required" });
      return;
    }
    const validOutputColumns = ["pac", "p_total", "vac1", "vac2", "vac3", "iac1", "iac2", "iac3", "fac"];
    if (!validOutputColumns.includes(columnType)) {
      Swal.fire({ icon: "warning", title: "Invalid Column Type", text: "For Output Day, please select PAC, P Total, VAC1-3, IAC1-3, or FAC" });
      return;
    }
    sendCommand("inverter-output-day", { date: commandDate, lan: language, column: columnType });
  };

  const handleGetInputDay = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date are required" });
      return;
    }
    if (columnType !== "vpv" && columnType !== "ipv") {
      Swal.fire({ icon: "warning", title: "Invalid Column Type", text: "For Input Day, please select either VPV or IPV column type" });
      return;
    }
    sendCommand("inverter-input-day", { date: commandDate, lan: language, column: columnType });
  };

  const handleGetMonth = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date (YYYY-MM) are required" });
      return;
    }
    sendCommand("inverter-month", { date: commandDate, lan: language });
  };

  const handleGetYear = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date (YYYY) are required" });
      return;
    }
    sendCommand("inverter-year", { date: commandDate, lan: language });
  };

  const handleGetTotal = () => {
    if (!inverterSN) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN is required" });
      return;
    }
    sendCommand("inverter-total", { lan: language });
  };

  const handleGetGridRealtime = () => {
    if (!inverterSN) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN is required" });
      return;
    }
    sendCommand("grid-realtime");
  };

  const handleGetGridDay = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date are required" });
      return;
    }
    sendCommand("grid-day", { date: commandDate, lan: language, column: "pac" });
  };

  const handleGetGridMonth = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date (YYYY-MM) are required" });
      return;
    }
    sendCommand("grid-month", { date: commandDate, lan: language });
  };

  const handleGetGridYear = () => {
    if (!inverterSN || !commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN and Date (YYYY) are required" });
      return;
    }
    sendCommand("grid-year", { date: commandDate, lan: language });
  };

  const handleGetGridTotal = () => {
    if (!inverterSN) {
      Swal.fire({ icon: "warning", title: "Required", text: "Inverter SN is required" });
      return;
    }
    sendCommand("grid-total", { lan: language });
  };

  const handleGetEvents = () => {
    if (!commandDate) {
      Swal.fire({ icon: "warning", title: "Required", text: "Date is required for events" });
      return;
    }
    sendCommand("events", { 
      plantId: deviceSN,  // Using device_id as plantId
      sdate: commandDate, 
      edate: commandDate, 
      type: 1, 
      page: 1, 
      limit: 10, 
      lan: language 
    });
  };

  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredData.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <div className={styles.fleet_view_cantaint}>
      {loading.global && <Loader />}

      {/* Header */}
      <section className={styles.topbar}>
        <div className={styles.container}>
          <img className={styles.topbar_logo} src={logoSvg} alt="Logo" />
        </div>
      </section>

      <section className="thermostat-details electric-vehicle-details">
        <Container>
          <h2 className={styles.main_title}>SolArk Details</h2>

          {/* FILTER UI */}
          <div className="row g-2 mt-3 mb-2 align-items-end">
            {/* Start Date */}
            <div className="col-6 col-md-3">
              <label className="small">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* Start Hour */}
            <div className="col-6 col-md-2">
              <label className="small">Start Hour</label>
              <select
                className="form-control form-control-sm"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hr = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hr}:00`}>
                      {hr}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* End Date */}
            <div className="col-6 col-md-3">
              <label className="small">End Date</label>
              <input
                type="date"
                min={startDate}
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* End Hour */}
            <div className="col-6 col-md-2">
              <label className="small">End Hour</label>
              <select
                className="form-control form-control-sm"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hr = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hr}:00`}>
                      {hr}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Utility Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">Utility</label>
              <Select
                options={[
                  { value: "All", label: "All Utility" },
                  ...(contextUtilities || []).map((utility) => ({
                    value: utility.id.toString(),
                    label: utility.utility_name,
                  })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedUtility === "All"
                    ? { value: "All", label: "All Utility" }
                    : (contextUtilities || []).map(u => ({ value: u.id.toString(), label: u.utility_name })).find(opt => opt.value === selectedUtility)
                }
                onChange={(option) => setSelectedUtility(option ? option.value : "All")}
                placeholder="Select Utility"
                isSearchable
                isClearable
              />
            </div> */}

            {/* State Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">State</label>
              <Select
                options={[
                  { value: "All", label: "All States" },
                  ...US_STATES.map((state) => ({ value: state, label: state })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedState === "All"
                    ? { value: "All", label: "All States" }
                    : { value: selectedState, label: selectedState }
                }
                onChange={(option) => setSelectedState(option ? option.value : "All")}
                placeholder="Select State"
                isSearchable
                isClearable
              />
            </div> */}

            {/* Buttons */}
            <div className="col-12 col-md-2 d-flex gap-2 mt-md-0 mt-2">
              <button
                className="btn btn-sm filter-button"
                style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                onClick={handleFilter}
              >
                Filter
              </button>
              <button
                className="btn btn-secondary btn-sm filter-button"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>

          {dateError && <div style={{ color: "red" }}>{dateError}</div>}

          {/* TABLE */}
          <div className="table-responsive mt-4">
            <Table className={styles.table}>
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th className="text-center">Name</th>
                  <th className="text-center">Inverters</th>
                  <th className="text-center">Production (kWh)</th>
                  <th className="text-center">Consumption (kWh)</th>
                  <th className="text-center">Battery State</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {currentPosts.length > 0 ? (
                  currentPosts.map((device, index) => (
                    <tr key={index}>
                      <td>{device.plant_id}</td>
                      <td className="text-center">{device.name}</td>
                      <td className="text-center">
                        {device.inverters && device.inverters.length > 0
                          ? device.inverters.map(inv => inv.sn).join(", ")
                          : "N/A"}
                      </td>
                      <td className="text-center">{device.total_production}</td>
                      <td className="text-center">{device.total_consumption}</td>
                      <td className="text-center">{device.battery_state || 'Unknown'}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                          onClick={() => handleOpenModal(device)}
                        >
                          Commands
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <PaginationComponent
            currentPage={currentPage}
            totalPages={Math.ceil(filteredData.length / postsPerPage)}
            onPageChange={setCurrentPage}
          />
        </Container>
      </section>

      {/* -------------------- COMMANDS MODAL -------------------- */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Device Commands</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDevice && (
            <div>
              <p>
                <strong>Name:</strong> {selectedDevice.name}
              </p>
              <p>
                <strong>Installer:</strong> {selectedDevice.installer}
              </p>

              {/* Common Parameters */}
              <h6 className="mt-4 mb-3">Device Information</h6>
              <div className="mb-3">
                <label className="form-label">
                  Device ID <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={deviceSN}
                  readOnly
                  disabled
                  placeholder="Device ID"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Inverters <span style={{ color: "red" }}>*</span>
                </label>
                {selectedDevice?.inverters && selectedDevice.inverters.length > 1 ? (
                  <select
                    className="form-control"
                    value={inverterSN}
                    onChange={(e) => setInverterSN(e.target.value)}
                  >
                    {selectedDevice.inverters.map((inv, idx) => (
                      <option key={idx} value={inv.sn}>
                        {inv.sn}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={inverterSN}
                    readOnly
                    disabled
                    placeholder="Inverter SN"
                  />
                )}
              </div>

              {/* <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={commandDate}
                    onChange={(e) => setCommandDate(e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">Column Type</label>
                  <select
                    className="form-control form-control-sm"
                    value={columnType}
                    onChange={(e) => setColumnType(e.target.value)}
                  >
                    <option value="pac">PAC</option>
                    <option value="p_total">P Total</option>
                    <option value="vac1">VAC1</option>
                    <option value="vac2">VAC2</option>
                    <option value="vac3">VAC3</option>
                    <option value="iac1">IAC1</option>
                    <option value="iac2">IAC2</option>
                    <option value="iac3">IAC3</option>
                    <option value="fac">FAC</option>
                    <option value="vpv">VPV</option>
                    <option value="ipv">IPV</option>
                  </select>
                </div>
              </div> */}

              {/* Inverter Management */}
              <h6 className="mt-4 mb-3">Inverter Management</h6>
              {/* <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="secondary" className="w-100" onClick={handleInvertersCount}>
                    Inverters Count
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="secondary" className="w-100" onClick={handleInvertersList}>
                    Inverters List
                  </Button>
                </div>
              </div> */}
              <div className="mb-3">
                <label className="form-label small">New Alias</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={newAlias}
                  onChange={(e) => setNewAlias(e.target.value)}
                  placeholder="Enter new alias"
                />
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="primary" className="w-100" onClick={handleSetAlias}>
                    Set Alias
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="danger" className="w-100" onClick={handleDeleteInverter}>
                    Delete Inverter
                  </Button>
                </div>
              </div>

              {/* Realtime Data */}
              {/* <h6 className="mt-4 mb-3">Realtime Data</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="info" className="w-100" onClick={handleGetRealtimeOutput}>
                    Get AC Output
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="info" className="w-100" onClick={handleGetRealtimeInput}>
                    Get DC Input
                  </Button>
                </div>
              </div> */}

              {/* Inverter Charts */}
              {/* <h6 className="mt-4 mb-3">Inverter Charts</h6>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <Button variant="success" className="w-100" onClick={handleGetOutputDay}>
                    Output Day
                  </Button>
                </div>
                <div className="col-4">
                  <Button variant="success" className="w-100" onClick={handleGetInputDay}>
                    Input Day
                  </Button>
                </div>
                <div className="col-4">
                  <Button variant="success" className="w-100" onClick={handleGetMonth}>
                    Month Chart
                  </Button>
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="success" className="w-100" onClick={handleGetYear}>
                    Year Chart
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="success" className="w-100" onClick={handleGetTotal}>
                    Total Lifetime
                  </Button>
                </div>
              </div> */}

              {/* Grid Commands */}
              {/* <h6 className="mt-4 mb-3">Grid Commands</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="warning" className="w-100" onClick={handleGetGridRealtime}>
                    Grid Realtime
                  </Button>
                </div>
                <div className="col-6">
                  <Button variant="warning" className="w-100" onClick={handleGetGridDay}>
                    Grid Day
                  </Button>
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <Button variant="warning" className="w-100" onClick={handleGetGridMonth}>
                    Grid Month
                  </Button>
                </div>
                <div className="col-4">
                  <Button variant="warning" className="w-100" onClick={handleGetGridYear}>
                    Grid Year
                  </Button>
                </div>
                <div className="col-4">
                  <Button variant="warning" className="w-100" onClick={handleGetGridTotal}>
                    Grid Total
                  </Button>
                </div>
              </div> */}

              {/* Event Logs */}
              {/* <h6 className="mt-4 mb-3">Event Logs</h6>
              <div className="row g-2 mb-3">
                <div className="col-12">
                  <Button variant="dark" className="w-100" onClick={handleGetEvents}>
                    Get Events
                  </Button>
                </div>
              </div> */}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SolArkDetails;
